import { useIntl } from "@components/Intl"
import {
  ArticlesQuery,
  ChoicesArticle,
  ChoicesComponentFilter,
} from "@generated"
import {
  ComponentFilter,
  CutFilter,
  SizeFilter,
  StyleFilter,
} from "@globalTypes"
import { getGraphQLClient } from "@lib/graphql-client"
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query"
import { useRouter } from "next/router"
import * as qs from "query-string"
import React, { useEffect, useState } from "react"
import LoadMore from "../LoadMore"
import Filters from "./Filters"
import Grid from "./Grid"
import Sidebar from "./Sidebar"
import { uidToComponents } from "./filterOptions"

export interface FilterState {
  style: string
  size: string | null
  cut: string | null
}

interface Props {
  uid: string
  prefix: string
  href: string
  title: string
}

const LIMIT = 40
const { sdk } = getGraphQLClient()

const ArticleGrid: React.FC<Props> = ({ prefix, title, href, uid }) => {
  const router = useRouter()
  const { messages } = useIntl()
  const [sidebarActive, setSidebarActive] = useState(false)
  const [queryData, setQueryData] = useState<InfiniteData<ArticlesQuery>>()
  const [state, setState] = useState<FilterState>({
    style: (router && (router.query["style"] as string)) || "classic",
    size: (router && (router.query["size"] as string)) || null,
    cut: (router && (router.query["cut"] as string)) || null,
  })

  const filters = {
    component: ComponentFilter[uidToComponents[uid]] as ChoicesComponentFilter,
    style: StyleFilter[state.style] || StyleFilter["Classic"],
    size: state.size ? SizeFilter[state.size] : undefined,
    cut: state.cut ? CutFilter[state.cut] : undefined,
  }

  const { hasNextPage, fetchNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery(
      // TODO: double check key on overview page with same filters
      ["articles", filters],
      ({ pageParam }) =>
        sdk.Articles({
          filter: filters,
          pagination: {
            limit: LIMIT,
            skip: pageParam || undefined,
          },
        }),
      {
        getNextPageParam: (lastPage) => {
          const { items } = lastPage.choicesArticles
          if (items.length < LIMIT) {
            return undefined
          }
          return items[items.length - 1].id
        },
        onSuccess(data) {
          setQueryData(data)
        },
      }
    )
  // Flatten the array and filter nulls
  const pages = queryData?.pages ?? []
  const items: ChoicesArticle[] = []

  for (const page of pages) {
    for (const edge of page?.choicesArticles.items) {
      items.push(edge as ChoicesArticle)
    }
  }

  useEffect(() => {
    if (router && router.asPath) {
      const split = router.asPath.split("?")
      if (split[1]) {
        const parsed = qs.parse(split[1])
        setState({
          style: (parsed && (parsed["style"] as string)) || "classic",
          size: (parsed && (parsed["size"] as string)) || null,
          cut: (parsed && (parsed["cut"] as string)) || null,
        })
      }
    }
  }, []) // eslint-disable-line

  const handleStyle = (style) => {
    const newState = {
      style,
      size: null,
      cut: null,
    }
    setState(newState)
    urlSync(newState)
  }
  const handleSize = (size) => {
    const newState = { ...state, size: state.size === size ? null : size }
    setState(newState)
    urlSync(newState)
  }
  const handleCut = (cut) => {
    const newState = { ...state, cut: state.cut === cut ? null : cut }
    setState(newState)
    urlSync(newState)
  }
  const handleReset = () => {
    const newState = {
      style: StyleFilter.classic,
      size: null,
      cut: null,
    }
    setState(newState)
    urlSync(newState)
  }

  function urlSync(state: FilterState) {
    let args = {
      style: state.style,
    }
    if (state.size) {
      args["size"] = state.size
    }
    if (state.cut) {
      args["cut"] = state.cut
    }

    const url = `${window.location.pathname}?${qs.stringify(args)}`
    if (url !== `${window.location.pathname}${window.location.search}`) {
      router.replace(
        `/[lang]${messages.navigation.separateItemsDetail.url}`,
        {
          pathname: window.location.pathname,
          query: args,
        },
        {
          shallow: true,
        }
      )
    }
  }

  const hasMoreFiltersSet = !!state.cut

  const fixedFilters =
    queryData && queryData.pages && queryData.pages[0].choicesArticles
      ? queryData.pages[0].choicesArticles.fixedFilters
      : {}
  const dynamicFilters =
    queryData && queryData.pages && queryData.pages[0].choicesArticles
      ? queryData.pages[0].choicesArticles.dynamicFilters
      : {}

  let hasMoreFilters = false
  if (uid === "gemstones" && state.style !== StyleFilter.hippiechic) {
    hasMoreFilters = true
  }

  return (
    <>
      <>
        <Filters
          style={state.style}
          size={state.size}
          uid={uid}
          fixedFilters={fixedFilters}
          onToggleSidebar={() => setSidebarActive(true)}
          onToggleStyle={handleStyle}
          onToggleSize={handleSize}
          hasMoreFilters={hasMoreFilters}
          hasMoreFiltersSet={hasMoreFiltersSet}
        />
        <Grid
          loading={isFetching}
          fetchingMore={isFetchingNextPage}
          items={items}
          prefix={prefix}
          href={href}
          filters={state}
        />
        <Sidebar
          uid={uid}
          title={title.toLowerCase()}
          active={sidebarActive}
          cut={state.cut}
          onCloseSidebar={() => setSidebarActive(false)}
          onToggleCut={handleCut}
          onReset={handleReset}
          dynamicFilters={dynamicFilters}
        />
        <LoadMore
          loading={isFetchingNextPage}
          disabled={!hasNextPage}
          onClick={() => fetchNextPage()}
        />
      </>
    </>
  )
}

export default ArticleGrid
